import { useState } from "react";
import { setAuth } from "./lib/authFetch";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const SECURITY_QUESTIONS = [
  "전화번호",
  "이메일 주소",
  "차량번호",
];

// '이미 로그인했던 기기' 확인표 (2026-10-14) — 서버가 로그인 성공 때 줌. 닉네임별로 보관
// 이걸 보내는 기기는 남이 PIN을 마구 틀려 계정이 잠겨도 평소처럼 로그인됨 (server/auth.js 대입 방지 설명)
const devKey = (n) => `dgds-device:${String(n || "").trim().toLowerCase()}`;
const getDevice = (n) => { try { return localStorage.getItem(devKey(n)) || undefined; } catch { return undefined; } };
const setDevice = (n, t) => { try { if (t) localStorage.setItem(devKey(n), t); } catch {} };
const fmtWhen = (iso) => { const t = Date.parse(iso || ""); if (isNaN(t)) return ""; const d = new Date(t); return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

export default function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState("login"); // login, register, recover
  const [nickname, setNickname] = useState("");
  const [pin, setPin] = useState("");
  const [securityQ, setSecurityQ] = useState(SECURITY_QUESTIONS[0]);
  const [securityA, setSecurityA] = useState("");
  const [newPin, setNewPin] = useState("");
  const [recoverStep, setRecoverStep] = useState(1); // 1: 닉네임+답변, 2: 새 PIN 입력
  // 토큰 만료로 로그아웃된 경우 안내 (App 이 sessionStorage 에 남김)
  const [error, setError] = useState(() => { try { const m = sessionStorage.getItem("dgds-login-msg"); sessionStorage.removeItem("dgds-login-msg"); return m || ""; } catch { return ""; } });
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null); // 로그인 성공했지만 최근 실패 기록이 있을 때 한 번 보여줌

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nickname.trim()) { setError("닉네임을 입력해주세요"); return; }
    if (!pin || pin.length < 4 || pin.length > 30) { setError("PIN은 4자 이상 30자 미만으로 입력해주세요"); return; }
    if (mode === "register" && !securityA.trim()) { setError("보안 질문 답변을 입력해주세요"); return; }

    setLoading(true);
    setError("");
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register"
        ? { nickname: nickname.trim(), pin, securityQuestion: securityQ, securityAnswer: securityA.trim() }
        : { nickname: nickname.trim(), pin, deviceToken: getDevice(nickname) };
      const res = await fetch(`${BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.id) {
        localStorage.setItem("sjv3-user", JSON.stringify({ id: data.id, nickname: data.nickname }));
        setDevice(data.nickname || nickname, data.deviceToken);
        setAuth(data.token, data.tokenExp); // 30일 로그인 토큰
        if (data.recentFails && data.recentFails.count > 0) setNotice(data); // 확인 누르면 들어감
        else onLogin(data);
      } else {
        setError(data.error || "오류가 발생했습니다");
      }
    } catch (err) {
      setError("서버에 연결할 수 없습니다");
    }
    setLoading(false);
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    if (!nickname.trim()) { setError("닉네임을 입력해주세요"); return; }

    if (recoverStep === 1) {
      if (!securityA.trim()) { setError("보안 질문 답변을 입력해주세요"); return; }
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${BASE}/auth/verify-security`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ nickname: nickname.trim(), securityQuestion: securityQ, securityAnswer: securityA.trim(), deviceToken: getDevice(nickname) }),
        });
        const data = await res.json();
        if (res.ok && data.verified) {
          setSecurityQ(data.question || "");
          setRecoverStep(2);
          setError("");
        } else {
          setError(data.error || "보안 질문 답변이 일치하지 않습니다");
        }
      } catch { setError("서버에 연결할 수 없습니다"); }
      setLoading(false);
    } else {
      if (!newPin || newPin.length < 4 || newPin.length > 30) { setError("새 PIN은 4자 이상 30자 미만으로 입력해주세요"); return; }
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${BASE}/auth/reset-pin`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ nickname: nickname.trim(), securityAnswer: securityA.trim(), newPin, deviceToken: getDevice(nickname) }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setSuccess("PIN이 재설정되었습니다. 새 PIN으로 로그인하세요.");
          setTimeout(() => {
            setMode("login");
            setPin("");
            setSecurityA("");
            setNewPin("");
            setRecoverStep(1);
            setSuccess("");
          }, 2000);
        } else {
          setError(data.error || "PIN 재설정에 실패했습니다");
        }
      } catch { setError("서버에 연결할 수 없습니다"); }
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setSuccess("");
    setRecoverStep(1);
    setSecurityA("");
    setNewPin("");
  };

  const inputStyle = { width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "var(--text)", padding: "12px 16px", fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-base)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 380, padding: "40px 36px", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 20 }}>
        {/* 로고 */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ width: 3, height: 28, background: "linear-gradient(180deg, #f0c040, #d4a017)", borderRadius: 2 }} />
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#f0c040" }}>DGDS</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-sub)" }}>AI 기반 주식 분석 플랫폼</div>
        </div>

        {notice && (() => {
          const f = notice.recentFails;
          return (
            <div style={{ marginBottom: 8 }}>
              <div style={{ padding: "16px 16px", borderRadius: 12, background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", color: "var(--text)", fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: "#fbbf24", marginBottom: 6 }}>최근 로그인 실패 기록이 있습니다</div>
                이 계정에 PIN·보안질문을 틀린 시도가 <b>{f.count}번</b> 있었습니다{f.last ? ` (마지막 ${fmtWhen(f.last)})` : ""}.
                {f.lockedUntil && <> 처음 보는 기기에서는 {fmtWhen(f.lockedUntil)}까지 잠겨 있고, 이 기기는 전에 로그인한 기기라 그대로 들어왔습니다.</>}
                <div style={{ marginTop: 6, color: "var(--text-sub)", fontSize: 12 }}>본인이 아니라면 로그아웃 후 「PIN 찾기」에서 PIN을 바꿔 주세요.</div>
              </div>
              <button onClick={() => onLogin(notice)} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #f0c040, #d4a017)", color: "#0d1117", fontSize: 15, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>확인하고 들어가기</button>
            </div>
          );
        })()}

        {!notice && <>
        {/* 탭 전환 */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
          <button onClick={() => switchMode("login")} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: mode === "login" ? "rgba(240,192,64,0.15)" : "rgba(255,255,255,0.03)", color: mode === "login" ? "#f0c040" : "#4a5568", fontSize: 14, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
            로그인
          </button>
          <button onClick={() => switchMode("register")} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: mode === "register" ? "rgba(167,139,250,0.15)" : "rgba(255,255,255,0.03)", color: mode === "register" ? "#a78bfa" : "#4a5568", fontSize: 14, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
            새로 시작하기
          </button>
          <button onClick={() => switchMode("recover")} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: mode === "recover" ? "rgba(56,189,248,0.15)" : "rgba(255,255,255,0.03)", color: mode === "recover" ? "#38bdf8" : "#4a5568", fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
            PIN 찾기
          </button>
        </div>

        {/* 로그인 / 가입 폼 */}
        {mode !== "recover" && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>닉네임</div>
              <input type="text" value={nickname} onChange={e => setNickname(e.target.value)} placeholder="닉네임을 입력하세요" style={inputStyle} autoFocus />
            </div>
            <div style={{ marginBottom: mode === "register" ? 16 : 24 }}>
              <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>PIN 코드 (4~30자)</div>
              <input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder="PIN을 입력하세요" maxLength={30} style={{ ...inputStyle, letterSpacing: "0.3em" }} />
              <div style={{ fontSize: 10, color: "var(--text-sub)", marginTop: 4 }}>숫자만 또는 문자로만 조합해도 됩니다</div>
            </div>

            {/* 가입 시 보안 질문 */}
            {mode === "register" && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>보안 질문 (PIN 분실 시 사용)</div>
                  <select value={securityQ} onChange={e => setSecurityQ(e.target.value)} style={{ ...inputStyle, cursor: "pointer", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234a5568' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
                    {SECURITY_QUESTIONS.map(q => <option key={q} value={q} style={{ background: "#1a1f2e", color: "var(--text)" }}>{q}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>보안 질문 답변</div>
                  <input type="text" value={securityA} onChange={e => setSecurityA(e.target.value)} placeholder="답변을 입력하세요" style={inputStyle} />
                </div>
              </>
            )}

            {error && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(251,113,133,0.08)", border: "1px solid rgba(251,113,133,0.2)", color: "#fb7185", fontSize: 12, marginBottom: 16, textAlign: "center" }}>{error}</div>
            )}

            <button type="submit" disabled={loading} style={{
              width: "100%", padding: "14px", borderRadius: 12, border: "none",
              background: mode === "login" ? "linear-gradient(135deg, #f0c040, #d4a017)" : "linear-gradient(135deg, #a78bfa, #7c3aed)",
              color: mode === "login" ? "#0d1117" : "#fff",
              fontSize: 15, fontWeight: 700, fontFamily: "inherit", cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}>
              {loading ? "처리 중..." : mode === "login" ? "로그인" : "가입하기"}
            </button>
          </form>
        )}

        {/* PIN 찾기 폼 */}
        {mode === "recover" && (
          <form onSubmit={handleRecover}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>닉네임</div>
              <input type="text" value={nickname} onChange={e => setNickname(e.target.value)} placeholder="가입 시 사용한 닉네임" style={inputStyle} autoFocus disabled={recoverStep === 2} />
            </div>
            {recoverStep === 1 && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>보안 질문</div>
                  <select value={securityQ} onChange={e => setSecurityQ(e.target.value)} style={{ ...inputStyle, cursor: "pointer", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234a5568' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
                    {SECURITY_QUESTIONS.map(q => <option key={q} value={q} style={{ background: "#1a1f2e", color: "var(--text)" }}>{q}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>답변</div>
                  <input type="text" value={securityA} onChange={e => setSecurityA(e.target.value)} placeholder="가입 시 입력한 답변" style={inputStyle} />
                </div>
              </>
            )}
            {recoverStep === 2 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, color: "#38bdf8", marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.2)", textAlign: "center" }}>
                  &#10003; 보안 질문 확인 완료. 새 PIN을 설정하세요.
                </div>
                <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>새 PIN 코드 (4~30자)</div>
                <input type="password" value={newPin} onChange={e => setNewPin(e.target.value)} placeholder="새 PIN을 입력하세요" maxLength={30} style={{ ...inputStyle, letterSpacing: "0.3em" }} autoFocus />
              </div>
            )}

            {error && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(251,113,133,0.08)", border: "1px solid rgba(251,113,133,0.2)", color: "#fb7185", fontSize: 12, marginBottom: 16, textAlign: "center" }}>{error}</div>
            )}
            {success && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.2)", color: "#34d399", fontSize: 12, marginBottom: 16, textAlign: "center" }}>{success}</div>
            )}

            <button type="submit" disabled={loading} style={{
              width: "100%", padding: "14px", borderRadius: 12, border: "none",
              background: "linear-gradient(135deg, #38bdf8, #0284c7)",
              color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "inherit",
              cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1,
            }}>
              {loading ? "처리 중..." : recoverStep === 1 ? "확인" : "PIN 재설정"}
            </button>
          </form>
        )}

        </>}

        {/* 비로그인 접속 */}
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={() => onLogin(null)} style={{ background: "none", border: "none", color: "var(--text-sub)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
            로그인 없이 둘러보기
          </button>
        </div>

        {/* 안내 */}
        <div style={{ marginTop: 24, padding: "12px 14px", borderRadius: 10, background: mode === "recover" ? "rgba(56,189,248,0.06)" : mode === "register" ? "rgba(167,139,250,0.06)" : "rgba(240,192,64,0.06)", border: `1px solid ${mode === "recover" ? "rgba(56,189,248,0.12)" : mode === "register" ? "rgba(167,139,250,0.12)" : "rgba(240,192,64,0.12)"}` }}>
          <div style={{ fontSize: 11, color: mode === "recover" ? "#38bdf8" : mode === "register" ? "#a78bfa" : "#f0c040", marginBottom: 4, fontWeight: 600 }}>안내</div>
          <div style={{ fontSize: 11, color: "#718096", lineHeight: 1.7 }}>
            {mode === "register"
              ? "닉네임, PIN, 보안 질문으로 간편하게 가입할 수 있습니다. 보안 질문은 PIN 분실 시 재설정에 사용됩니다."
              : mode === "recover"
              ? "가입 시 설정한 보안 질문 답변으로 본인 확인 후 새 PIN을 설정할 수 있습니다."
              : "가입 시 설정한 닉네임과 PIN으로 로그인하세요. 어떤 기기에서든 내 매매일지를 볼 수 있습니다."
            }
          </div>
        </div>
      </div>
    </div>
  );
}
